import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatContractDate, getCurrentMadridISO } from '../../utils/dateUtils';
import contractsConfig from '../../config/contractsConfig';
import LoadingSpinner from '../LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractsManager = () => {
  const [contracts, setContracts] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractType, setContractType] = useState('');
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [buyers, setBuyers] = useState([{ name: '', email: '', dni: '', buyer_order: 1, address: '' }]); // Added address field
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signingContract, setSigningContract] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // New state for form steps
  
  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Signer email edit states
  const [editingSignerEmail, setEditingSignerEmail] = useState(null); // {contractId, signerIndex, currentEmail}
  const [newSignerEmail, setNewSignerEmail] = useState('');
  const [resendingEmail, setResendingEmail] = useState(null); // {contractId, signerIndex}

  const loadContracts = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      // Agregar parámetro de cache-bust si se fuerza refresh
      const url = `${API}/cms/contracts${forceRefresh ? `?t=${Date.now()}` : ''}`;
      
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': forceRefresh ? 'no-cache' : 'max-age=300'
        },
        timeout: 15000 // Aumentar timeout a 15 segundos
      });
      
      if (response.data) {
        // Los contratos ya vienen ordenados del backend por fecha de creación descendente
        setContracts(response.data);
      }
    } catch (error) {
      console.error('Error loading contracts:', error);
      if (error.response?.status === 401) {
        // Token expirado, redirigir al login
        localStorage.removeItem('token');
        window.location.href = '/cms';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cms/pending-payments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingPayments(response.data || []);
    } catch (error) {
      console.error('Error loading pending payments:', error);
    }
  }, []);

  useEffect(() => {
    loadContracts();
    loadPendingPayments();
  }, [loadContracts, loadPendingPayments]);

  // Functions for signer email management
  const handleUpdateSignerEmail = async (contractId, signerIndex) => {
    if (!newSignerEmail.trim()) {
      alert('Por favor, introduce un email válido');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newSignerEmail)) {
      alert('El formato del email no es válido');
      return;
    }
    
    try {
      await axios.put(
        `${API}/cms/contracts/${contractId}/signers/${signerIndex}/email`,
        { new_email: newSignerEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert('✅ Email actualizado correctamente');
      setEditingSignerEmail(null);
      setNewSignerEmail('');
      await loadContracts(true);
    } catch (error) {
      console.error('Error updating signer email:', error);
      alert('Error al actualizar el email: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  const handleResendSignerEmail = async (contractId, signerIndex, signerName) => {
    if (!window.confirm(`¿Enviar email de firma a ${signerName}?`)) {
      return;
    }
    
    setResendingEmail({ contractId, signerIndex });
    
    try {
      const response = await axios.post(
        `${API}/cms/contracts/${contractId}/signers/${signerIndex}/resend`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert(`✅ ${response.data.message}`);
    } catch (error) {
      console.error('Error resending email:', error);
      alert('Error al reenviar el email: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setResendingEmail(null);
    }
  };

  const getContractConfig = (contractType) => {
    return contractsConfig.contract_types.find(c => c.id === contractType);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending_signature': return 'bg-yellow-100 text-yellow-800';
      case 'partially_signed': return 'bg-orange-100 text-orange-800'; // New status
      case 'signed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'draft': return 'Borrador';
      case 'pending_signature': return 'Pendiente de Firma';
      case 'partially_signed': return 'Parcialmente Firmado'; // New status
      case 'signed': return 'Firmado';
      case 'completed': return 'Completado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return { valid: false, message: 'Email es requerido' };
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Formato de email no válido' };
    }
    
    return { valid: true, message: '' };
  };

  const validateDNI = (dni) => {
    if (!dni) return { valid: false, message: 'DNI/NIE es requerido' };
    
    // Remove spaces and convert to uppercase
    const cleanDNI = dni.replace(/\s/g, '').toUpperCase();
    
    // Spanish DNI/NIE validation
    const dniRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    
    if (!dniRegex.test(cleanDNI) && !nieRegex.test(cleanDNI)) {
      return { valid: false, message: 'Formato DNI/NIE no válido' };
    }
    
    // DNI validation logic
    if (dniRegex.test(cleanDNI)) {
      const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
      const number = parseInt(cleanDNI.substring(0, 8));
      const letter = cleanDNI.substring(8).toUpperCase();
      const expectedLetter = letters.charAt(number % 23);
      
      if (expectedLetter !== letter) {
        return { valid: false, message: 'Letra del DNI incorrecta' };
      }
      return { valid: true, message: '' };
    }
    
    // NIE validation logic
    if (nieRegex.test(cleanDNI)) {
      const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
      const prefix = cleanDNI.charAt(0);
      const nieDigits = cleanDNI.substring(1, 8); // Keep as string to preserve leading zeros
      const letter = cleanDNI.substring(8).toUpperCase();
      
      // Convert NIE prefix to number (X=0, Y=1, Z=2) and concatenate with digits
      const prefixNumber = prefix === 'X' ? '0' : prefix === 'Y' ? '1' : '2';
      const fullNumber = parseInt(prefixNumber + nieDigits);
      const expectedLetter = letters.charAt(fullNumber % 23);
      
      if (expectedLetter !== letter) {
        return { valid: false, message: 'Letra del NIE incorrecta' };
      }
      return { valid: true, message: '' };
    }
    
    // Also accept passport numbers (alphanumeric, 6-12 characters) for foreigners
    const passportRegex = /^[A-Z0-9]{6,12}$/i;
    if (passportRegex.test(cleanDNI)) {
      return { valid: true, message: '' };
    }
    
    return { valid: false, message: 'Formato DNI/NIE no válido' };
  };

  const validatePhone = (phone) => {
    // Spanish phone validation (with or without country code)
    const phoneRegex = /^(\+34|0034|34)?[6-9][0-9]{8}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const formatNumber = (value) => {
    // Format numbers with thousands separators
    if (!value) return '';
    const number = value.toString().replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleContractTypeChange = (type) => {
    setContractType(type);
    const config = getContractConfig(type);
    
    // Initialize form data with default values
    const initialData = {};
    config.fields.forEach(field => {
      initialData[field.id] = '';
    });
    
    setFormData(initialData);
    setValidationErrors({});
    setAttachments([]);
    setCurrentStep(1); // Reset to step 1
    
    // Reset buyers to single buyer when contract type changes
    setBuyers([{ name: '', email: '', dni: '', buyer_order: 1, address: '' }]);
  };

  const addBuyer = () => {
    const newBuyer = { 
      name: '', 
      email: '', 
      dni: '', 
      buyer_order: buyers.length + 1,
      address: '' // Add address field
    };
    setBuyers([...buyers, newBuyer]);
  };

  const removeBuyer = (index) => {
    if (buyers.length > 1) {
      const newBuyers = buyers.filter((_, i) => i !== index);
      // Update buyer_order for remaining buyers
      const updatedBuyers = newBuyers.map((buyer, i) => ({
        ...buyer,
        buyer_order: i + 1
      }));
      setBuyers(updatedBuyers);
    }
  };

  const updateBuyer = (index, field, value) => {
    const newBuyers = [...buyers];
    newBuyers[index][field] = value;
    setBuyers(newBuyers);
    
    // Clear validation error for this field
    const errorKey = `buyer_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    // Real-time validation for DNI/NIE
    if (field === 'dni' && value) {
      const dniValidation = validateDNI(value);
      if (!dniValidation.valid) {
        setValidationErrors(prev => ({
          ...prev,
          [errorKey]: dniValidation.message
        }));
      }
    }

    // Real-time validation for email
    if (field === 'email' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.valid) {
        setValidationErrors(prev => ({
          ...prev,
          [errorKey]: emailValidation.message
        }));
      }
    }
  };



  const handleNextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      if (!validateContractData()) {
        return;
      }
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const validateContractData = () => {
    const config = getContractConfig(contractType);
    if (!config) return false;

    const errors = {};
    
    // Validate contract fields
    config.fields.forEach(field => {
      const value = formData[field.id];
      if (field.required && (!value || value.toString().trim() === '')) {
        errors[field.id] = `${field.label} es requerido`;
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    setUploading(true);
    
    try {
      const newAttachments = [];
      
      for (const file of files) {
        // Validate file type
        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'image/jpeg',
          'image/png',
          'image/gif'
        ];
        
        if (!allowedTypes.includes(file.type)) {
          alert(`Tipo de archivo no permitido: ${file.name}`);
          continue;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`Archivo demasiado grande: ${file.name} (máximo 10MB)`);
          continue;
        }
        
        // Convert to base64
        const base64Content = await fileToBase64(file);
        
        const attachment = {
          filename: file.name,
          content: base64Content,
          content_type: file.type,
          size: file.size,
          uploaded_at: getCurrentMadridISO()
        };
        
        newAttachments.push(attachment);
      }
      
      setAttachments(prev => [...prev, ...newAttachments]);
      
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error al subir archivos');
    } finally {
      setUploading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleInputChange = (fieldId, value) => {
    // Clear previous error for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldId];
      return newErrors;
    });
    
    let processedValue = value;
    
    // Real-time validation and formatting
    if (fieldId.includes('email')) {
      const emailValidation = validateEmail(value);
      if (value && !emailValidation.valid) {
        setValidationErrors(prev => ({
          ...prev,
          [fieldId]: emailValidation.message
        }));
      }
    } else if (fieldId.includes('dni') || fieldId.includes('nie')) {
      const dniValidation = validateDNI(value);
      if (value && !dniValidation.valid) {
        setValidationErrors(prev => ({
          ...prev,
          [fieldId]: dniValidation.message
        }));
      }
    } else if (fieldId.includes('telefono') || fieldId.includes('phone')) {
      if (value && !validatePhone(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [fieldId]: 'Teléfono no válido'
        }));
      }
    } else if (fieldId.includes('precio') || fieldId.includes('importe') || fieldId.includes('cantidad')) {
      // Format numbers with thousands separators
      const numericValue = value.replace(/\D/g, '');
      if (numericValue) {
        processedValue = formatNumber(numericValue);
      }
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldId]: processedValue
      };
      
      // Auto-generate text versions for currency fields
      if ((fieldId.includes('precio') || fieldId.includes('importe') || fieldId.includes('cantidad')) && processedValue) {
        const numericValue = parseFloat(processedValue.replace(/\./g, ''));
        if (!isNaN(numericValue)) {
          newData[`${fieldId}_letras`] = convertCurrencyToWords(numericValue);
        }
      }
      
      return newData;
    });
  };

  const convertCurrencyToWords = (amount) => {
    // Simple conversion function (you can enhance this)
    if (amount === 0) return 'cero euros';
    
    const ones = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
    const tens = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const hundreds = ['', 'cien', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
    
    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        if (n === 100) result += 'cien';
        else result += hundreds[Math.floor(n / 100)];
        n %= 100;
        if (n > 0) result += ' ';
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)];
        n %= 10;
        if (n > 0) result += ' y ' + ones[n];
      } else if (n >= 10) {
        const teens = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
        result += teens[n - 10];
      } else if (n > 0) {
        result += ones[n];
      }
      return result;
    };
    
    if (amount >= 1000000) {
      const millions = Math.floor(amount / 1000000);
      let result = millions === 1 ? 'un millón' : convertHundreds(millions) + ' millones';
      amount %= 1000000;
      if (amount > 0) result += ' ';
      if (amount >= 1000) {
        const thousands = Math.floor(amount / 1000);
        result += thousands === 1 ? 'mil' : convertHundreds(thousands) + ' mil';
        amount %= 1000;
        if (amount > 0) result += ' ';
      }
      if (amount > 0) {
        result += convertHundreds(amount);
      }
      return result + ' euros';
    } else if (amount >= 1000) {
      const thousands = Math.floor(amount / 1000);
      let result = thousands === 1 ? 'mil' : convertHundreds(thousands) + ' mil';
      amount %= 1000;
      if (amount > 0) result += ' ' + convertHundreds(amount);
      return result + ' euros';
    } else {
      return convertHundreds(amount) + ' euros';
    }
  };


  const validateForm = () => {
    const errors = {};
    
    // Validate contract fields
    const config = getContractConfig(contractType);
    config.fields.forEach(field => {
      if (field.required && !formData[field.id]) {
        errors[field.id] = `${field.label} es requerido`;
      }
    });
    
    // Validate buyers
    buyers.forEach((buyer, index) => {
      if (!buyer.name.trim()) {
        errors[`buyer_${index}_name`] = 'El nombre del comprador es requerido';
      }
      if (!buyer.email.trim()) {
        errors[`buyer_${index}_email`] = 'El email del comprador es requerido';
      } else {
        const emailValidation = validateEmail(buyer.email);
        if (!emailValidation.valid) {
          errors[`buyer_${index}_email`] = emailValidation.message;
        }
      }
      if (!buyer.dni.trim()) {
        errors[`buyer_${index}_dni`] = 'El DNI/NIE del comprador es requerido';
      } else {
        const dniValidation = validateDNI(buyer.dni);
        if (!dniValidation.valid) {
          errors[`buyer_${index}_dni`] = dniValidation.message;
        }
      }
      if (!buyer.address.trim()) {
        errors[`buyer_${index}_address`] = 'La dirección del comprador es requerida';
      }
    });
    
    // Check for duplicate emails
    const emails = buyers.map(s => s.email.toLowerCase());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      errors['duplicate_emails'] = 'No puede haber emails duplicados entre los compradores';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const contractData = {
        contract_type: contractType,
        contract_data: formData,
        buyers: buyers.map(buyer => ({
          name: buyer.name,
          email: buyer.email,
          dni: buyer.dni,
          buyer_order: buyer.buyer_order,
          address: buyer.address
        })),
        attachments: attachments
      };
      
      await axios.post(`${API}/cms/contracts`, contractData);
      
      setShowForm(false);
      setContractType('');
      setFormData({});
      setValidationErrors({});
      setAttachments([]);
      setBuyers([{ name: '', email: '', dni: '', buyer_order: 1, address: '' }]);
      setCurrentStep(1); // Reset to step 1
      
      await loadContracts(true); // Forzar refresh para mostrar el nuevo contrato
      
      alert('Contrato creado exitosamente');
    } catch (error) {
      console.error('Error creating contract:', error);
      alert('Error al crear el contrato: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = async (contractId) => {
    setSigningContract(contractId);
    
    try {
      const adminData = {
        contract_id: contractId,
        signer_name: 'Administrador Vivendius',
        signer_email: 'administracio@vivendius.cat',
        signer_dni: 'B12345678'
      };
      
      await axios.post(`${API}/cms/contracts/${contractId}/sign`, adminData);
      await loadContracts(true); // Forzar refresh para mostrar el contrato firmado
      alert('Contrato firmado por el administrador');
    } catch (error) {
      console.error('Error signing contract:', error);
      alert('Error al firmar el contrato: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSigningContract(null);
    }
  };

  const handleDownloadAttachment = async (contractId, attachmentIndex, filename) => {
    setDownloadingAttachment(`${contractId}-${attachmentIndex}`);
    
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/attachments/${attachmentIndex}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error al descargar el documento');
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const handleDownloadPDF = async (contractId) => {
    setDownloadingPdf(contractId);
    
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar el PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const renderFormField = (field) => {
    const hasError = validationErrors[field.id];
    
    switch (field.type) {
      case 'date':
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="date"
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {hasError && <p className="text-red-500 text-sm mt-1">{hasError}</p>}
          </div>
        );
      
      case 'select':
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Seleccionar...</option>
              {field.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {hasError && <p className="text-red-500 text-sm mt-1">{hasError}</p>}
          </div>
        );
      
      case 'textarea':
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {hasError && <p className="text-red-500 text-sm mt-1">{hasError}</p>}
          </div>
        );
      
      case 'number':
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="number"
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              min={field.validation?.min}
              max={field.validation?.max}
              step="0.01"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {hasError && <p className="text-red-500 text-sm mt-1">{hasError}</p>}
          </div>
        );
      
      default:
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <input
              type="text"
              value={formData[field.id] || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              readOnly={field.auto_generated}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                hasError ? 'border-red-500' : 'border-gray-300'
              } ${field.auto_generated ? 'bg-gray-50' : ''}`}
            />
            {hasError && <p className="text-red-500 text-sm mt-1">{hasError}</p>}
          </div>
        );
    }
  };

  const sendSignatureEmails = async (contractId) => {
    try {
      setSendingEmails(contractId);
      await axios.post(`${API}/cms/contracts/${contractId}/send-signature-emails`);
      alert('Emails de firma enviados exitosamente');
    } catch (error) {
      console.error('Error sending signature emails:', error);
      alert('Error al enviar emails: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSendingEmails(null);
    }
  };

  const handleDeleteContract = async (contractId, contractType) => {
    const contractName = getContractConfig(contractType)?.name || contractType;
    
    if (!window.confirm(`¿Estás seguro de que quieres eliminar el contrato ${contractName} (ID: ${contractId.substr(0, 8)}...)?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/cms/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      await loadContracts(true); // Forzar refresh para actualizar la lista
      alert('Contrato eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Error al eliminar el contrato: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  const handleGeneratePaymentLink = async (contractId, paymentType = 'arras') => {
    try {
      const response = await axios.post(`${API}/payments/create-transfer-instructions`, {
        contract_id: contractId,
        payment_type: paymentType
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const paymentUrl = response.data.payment_url;
      const paymentReference = response.data.payment_reference;
      const amount = response.data.amount;
      
      // Set payment data and show modal
      setPaymentData({
        url: paymentUrl,
        reference: paymentReference,
        amount: amount,
        paymentType: paymentType,
        contractId: contractId
      });
      setShowPaymentModal(true);
      
    } catch (error) {
      console.error('Error generating payment link:', error);
      alert('Error al generar enlace de pago: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000); // Reset after 3 seconds
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (fallbackErr) {
        console.error('Failed to copy text: ', fallbackErr);
        alert('No se pudo copiar automáticamente. Por favor, copia manualmente el enlace.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleMarkPaymentAsPaid = async (transactionId) => {
    if (!window.confirm('¿Confirmas que has recibido la transferencia para este contrato?')) {
      return;
    }

    try {
      await axios.post(`${API}/payments/${transactionId}/mark-as-paid`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      await loadContracts(true); // Refresh contracts
      await loadPendingPayments(); // Refresh pending payments
      alert('✅ Pago marcado como recibido exitosamente');
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      alert('Error al marcar el pago: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  const getBuyerSignatureUrl = (contractId, signer) => {
    const base = `${window.location.origin}/contracts/${contractId}/sign`;
    if (signer && signer.email) {
      const params = new URLSearchParams();
      params.set('email', signer.email);
      if (signer.dni) params.set('dni', signer.dni);
      return `${base}?${params.toString()}`;
    }
    return base;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Contratos</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
        >
          Nuevo Contrato
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Crear Nuevo Contrato</h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Contrato *
                  </label>
                  <select
                    value={contractType}
                    onChange={(e) => handleContractTypeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Seleccionar tipo de contrato...</option>
                    {contractsConfig.contract_types.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                {contractType && (
                  <div>
                    {/* Progress Steps */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                            currentStep >= 1 ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 text-gray-500'
                          }`}>
                            1
                          </div>
                          <span className={`ml-2 text-sm font-medium ${
                            currentStep >= 1 ? 'text-purple-600' : 'text-gray-500'
                          }`}>
                            Datos del Contrato
                          </span>
                        </div>
                        <div className="flex-1 h-px bg-gray-300 mx-4"></div>
                        <div className="flex items-center">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                            currentStep >= 2 ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 text-gray-500'
                          }`}>
                            2
                          </div>
                          <span className={`ml-2 text-sm font-medium ${
                            currentStep >= 2 ? 'text-purple-600' : 'text-gray-500'
                          }`}>
                            Compradores
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Step 1: Contract Data */}
                    {currentStep === 1 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Paso 1: Datos del Contrato</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {getContractConfig(contractType).fields.map(field => renderFormField(field))}
                        </div>
                        
                        {/* Attachments section */}
                        <div className="mt-8 border-t pt-6">
                          <h4 className="text-md font-medium text-gray-900 mb-4">Documentos Adjuntos</h4>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                            <div className="text-center">
                              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <div className="mt-4">
                                <label htmlFor="file-upload" className="cursor-pointer">
                                  <span className="mt-2 block text-sm font-medium text-gray-900">
                                    Adjuntar documentos
                                  </span>
                                  <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileUpload} multiple />
                                </label>
                                <p className="mt-1 text-sm text-gray-500">
                                  PDF, Word, imágenes (máx. 10MB cada uno)
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Display uploaded attachments */}
                          {attachments.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Archivos seleccionados:</h5>
                              <div className="space-y-2">
                                {attachments.map((attachment, index) => (
                                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                                    <div className="flex items-center">
                                      <svg className="h-5 w-5 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-sm text-gray-900">{attachment.filename}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        ({(attachment.size / 1024 / 1024).toFixed(2)} MB)
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeAttachment(index)}
                                      className="text-red-600 hover:text-red-800 text-sm"
                                    >
                                      Eliminar
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Navigation buttons */}
                        <div className="mt-8 flex justify-between">
                          <button
                            type="button"
                            onClick={() => setShowForm(false)}
                            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={handleNextStep}
                            className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors"
                          >
                            Siguiente: Compradores →
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Buyers */}
                    {currentStep === 2 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Paso 2: Compradores del Contrato</h3>
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-sm text-blue-800">
                            💡 Defina aquí el número de compradores y sus datos. El sistema detectará automáticamente cuántas firmas son necesarias.
                          </p>
                        </div>
                        
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-medium text-gray-900">
                            Compradores ({buyers.length} {buyers.length === 1 ? 'comprador' : 'compradores'})
                          </h4>
                          <button
                            type="button"
                            onClick={addBuyer}
                            className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors"
                          >
                            + Añadir Comprador
                          </button>
                        </div>
                        
                        {validationErrors.duplicate_emails && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{validationErrors.duplicate_emails}</p>
                          </div>
                        )}
                        
                        {buyers.map((buyer, index) => (
                          <div key={index} className="mb-6 p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">Comprador {index + 1}</h4>
                              {buyers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeBuyer(index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Eliminar
                                </button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Nombre completo *
                                </label>
                                <input
                                  type="text"
                                  value={buyer.name}
                                  onChange={(e) => updateBuyer(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Nombre completo del comprador"
                                />
                                {validationErrors[`buyer_${index}_name`] && (
                                  <p className="text-sm text-red-600 mt-1">{validationErrors[`buyer_${index}_name`]}</p>
                                )}
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  DNI/NIE *
                                </label>
                                <input
                                  type="text"
                                  value={buyer.dni}
                                  onChange={(e) => updateBuyer(index, 'dni', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="DNI o NIE del comprador"
                                />
                                {validationErrors[`buyer_${index}_dni`] && (
                                  <p className="text-sm text-red-600 mt-1">{validationErrors[`buyer_${index}_dni`]}</p>
                                )}
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Email *
                                </label>
                                <input
                                  type="email"
                                  value={buyer.email}
                                  onChange={(e) => updateBuyer(index, 'email', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Email del comprador"
                                />
                                {validationErrors[`buyer_${index}_email`] && (
                                  <p className="text-sm text-red-600 mt-1">{validationErrors[`buyer_${index}_email`]}</p>
                                )}
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Domicilio *
                                </label>
                                <input
                                  type="text"
                                  value={buyer.address}
                                  onChange={(e) => updateBuyer(index, 'address', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Dirección completa del comprador"
                                />
                                {validationErrors[`buyer_${index}_address`] && (
                                  <p className="text-sm text-red-600 mt-1">{validationErrors[`buyer_${index}_address`]}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Navigation buttons */}
                        <div className="mt-8 flex justify-between">
                          <button
                            type="button"
                            onClick={handlePreviousStep}
                            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                          >
                            ← Anterior
                          </button>
                          <button
                            type="submit"
                            disabled={submitting}
                            className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                          >
                            {submitting ? 'Creando...' : 'Crear Contrato'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!contractType && (
                  <div className="flex justify-end space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Pending Payments Section */}
      {pendingPayments.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-medium text-yellow-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Pagos Pendientes ({pendingPayments.length})
          </h2>
          <div className="space-y-3">
            {pendingPayments.map((payment) => (
              <div key={payment.transaction_id} className="bg-white border border-yellow-300 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <span className="text-lg font-semibold text-green-600">
                        {payment.amount.toFixed(2)} EUR
                      </span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                        Esperando transferencia
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Contrato:</strong> {payment.contract_type.replace('_', ' ')} - {payment.contract_data.direccion_inmueble}</p>
                      <p><strong>Referencia:</strong> <code className="bg-gray-100 px-1 rounded">{payment.payment_reference}</code></p>
                      <p><strong>Creado:</strong> {new Date(payment.created_at).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleMarkPaymentAsPaid(payment.transaction_id)}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                      title="Marcar como pagado"
                    >
                      ✅ Confirmado
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-[800px] w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contrato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Compradores
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contracts.map((contract) => (
              <tr key={contract.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {getContractConfig(contract.contract_type)?.name || contract.contract_type}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {contract.id.substr(0, 8)}...
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-2">
                    {contract.buyers && contract.buyers.length > 0 ? (
                      contract.buyers.map((buyer, index) => (
                        <div key={index} className={`p-2 bg-gray-50 rounded-md ${index > 0 ? 'mt-2' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-grow">
                              <div className="font-medium text-gray-900 text-sm">{buyer.name}</div>
                              
                              {/* Email con opción de editar */}
                              {editingSignerEmail?.contractId === contract.id && editingSignerEmail?.signerIndex === index ? (
                                <div className="flex items-center gap-1 mt-1">
                                  <input
                                    type="email"
                                    value={newSignerEmail}
                                    onChange={(e) => setNewSignerEmail(e.target.value)}
                                    className="text-xs border rounded px-1 py-0.5 w-36"
                                    placeholder="nuevo@email.com"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleUpdateSignerEmail(contract.id, index)}
                                    className="text-green-600 hover:text-green-800 text-xs"
                                    title="Guardar"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => { setEditingSignerEmail(null); setNewSignerEmail(''); }}
                                    className="text-red-600 hover:text-red-800 text-xs"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-600 text-xs">{buyer.email}</span>
                                  {!buyer.signed_at && (contract.status === 'pending_signature' || contract.status === 'partially_signed') && (
                                    <button
                                      onClick={() => { 
                                        setEditingSignerEmail({ contractId: contract.id, signerIndex: index, currentEmail: buyer.email });
                                        setNewSignerEmail(buyer.email);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-xs ml-1"
                                      title="Editar email"
                                    >
                                      ✏️
                                    </button>
                                  )}
                                </div>
                              )}
                              
                              <div className="text-gray-500 text-xs">DNI: {buyer.dni}</div>
                              
                              {/* Botón de reenviar email */}
                              {!buyer.signed_at && (contract.status === 'pending_signature' || contract.status === 'partially_signed') && (
                                <button
                                  onClick={() => handleResendSignerEmail(contract.id, index, buyer.name)}
                                  disabled={resendingEmail?.contractId === contract.id && resendingEmail?.signerIndex === index}
                                  className="mt-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                  {resendingEmail?.contractId === contract.id && resendingEmail?.signerIndex === index ? (
                                    <>
                                      <span className="animate-spin">⏳</span>
                                      Enviando...
                                    </>
                                  ) : (
                                    <>📧 Reenviar email</>
                                  )}
                                </button>
                              )}
                            </div>
                            <div className="flex flex-col items-end text-xs">
                              <div className="text-gray-400">#{buyer.buyer_order}</div>
                              {buyer.signed_at ? (
                                <div className="text-green-600 font-medium">✓ Firmado</div>
                              ) : (
                                <div className="text-orange-600">Pendiente</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 text-sm italic">Sin compradores</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                    {getStatusText(contract.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatContractDate(contract.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex flex-col space-y-2">
                    {/* Primera fila de botones */}
                    <div className="flex flex-wrap gap-2">
                      {contract.status === 'draft' && (
                        <button
                          onClick={() => handleSign(contract.id)}
                          disabled={signingContract === contract.id}
                          className="text-purple-600 hover:text-purple-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 text-sm"
                        >
                          {signingContract === contract.id ? (
                            <>
                              <LoadingSpinner size="small" text="" color="text-purple-600" />
                              <span>Firmando...</span>
                            </>
                          ) : (
                            <span>Firmar</span>
                          )}
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDownloadPDF(contract.id)}
                        disabled={downloadingPdf === contract.id}
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 text-sm"
                      >
                        {downloadingPdf === contract.id ? (
                          <>
                            <LoadingSpinner size="small" text="" color="text-blue-600" />
                            <span>Descargando...</span>
                          </>
                        ) : (
                          <span>📄 PDF</span>
                        )}
                      </button>
                      
                      <button
                        onClick={() => {
                          // Determinar el tipo de pago según el tipo de contrato
                          let paymentType = 'arras'; // Por defecto
                          if (contract.contract_type === 'reserva_no_condicionada' || 
                              contract.contract_type === 'reserva_condicionada') {
                            paymentType = 'reserva';
                          }
                          handleGeneratePaymentLink(contract.id, paymentType);
                        }}
                        className="text-green-600 hover:text-green-900 flex items-center space-x-1 text-sm"
                        title="Generar enlace de pago"
                      >
                        <span>💳 Pago</span>
                      </button>
                      
                      <button
                        onClick={() => handleDeleteContract(contract.id, contract.contract_type)}
                        className="text-red-600 hover:text-red-900 flex items-center space-x-1 text-sm"
                        title="Eliminar contrato"
                      >
                        <span>🗑️ Eliminar</span>
                      </button>
                    </div>
                    
                    {/* Segunda fila - Email y enlaces de firma */}
                    {contract.status === 'pending_signature' && (
                      <div className="mt-2">
                        <button
                          onClick={() => sendSignatureEmails(contract.id)}
                          disabled={sendingEmails === contract.id}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            sendingEmails === contract.id
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                              : 'bg-yellow-600 text-white hover:bg-yellow-700'
                          }`}
                        >
                          {sendingEmails === contract.id ? 'Enviando...' : 'Enviar Emails a Compradores'}
                        </button>
                        <div className="text-xs text-gray-500 mt-3">
                          <div className="font-medium mb-2">Enlaces de firma:</div>
                          <div className="space-y-2">
                            {contract.buyers && contract.buyers.map((buyer, index) => (
                              <div key={index} className="bg-gray-50 p-2 rounded border">
                                <div className="font-medium text-gray-700 mb-1">{buyer.name}:</div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={getBuyerSignatureUrl(contract.id, buyer)}
                                    readOnly
                                    className="flex-1 text-xs border rounded px-2 py-1 bg-white"
                                  />
                                  <button
                                    onClick={() => navigator.clipboard.writeText(getBuyerSignatureUrl(contract.id, buyer))}
                                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                  >
                                    Copiar
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Attachments */}
                    {contract.attachments && contract.attachments.length > 0 && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Documentos adjuntos ({contract.attachments.length}):</span>
                        <div className="mt-1 space-y-1">
                          {contract.attachments.map((attachment, index) => (
                            <button
                              key={index}
                              onClick={() => handleDownloadAttachment(contract.id, index, attachment.filename)}
                              disabled={downloadingAttachment === `${contract.id}-${index}`}
                              className="block text-blue-600 hover:text-blue-900 truncate max-w-xs disabled:opacity-50 disabled:cursor-not-allowed"
                              title={attachment.filename}
                            >
                              {downloadingAttachment === `${contract.id}-${index}` ? (
                                <span className="flex items-center space-x-1">
                                  <LoadingSpinner size="small" text="" color="text-blue-600" />
                                  <span>⏳ {attachment.filename}</span>
                                </span>
                              ) : (
                                <span>📎 {attachment.filename}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Link Modal */}
      {showPaymentModal && paymentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Enlace de Pago SEPA Generado
                </h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Payment Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="text-3xl font-bold text-green-600">
                    {paymentData.amount.toLocaleString('es-ES')} EUR
                  </div>
                </div>
                <div className="text-center text-sm text-gray-600">
                  <div><strong>Tipo:</strong> {paymentData.paymentType === 'arras' ? 'Arras' : 'Reserva'}</div>
                  <div><strong>Referencia:</strong> {paymentData.reference}</div>
                  <div><strong>ID Contrato:</strong> {paymentData.contractId.substr(0, 8)}...</div>
                </div>
              </div>

              {/* Payment URL Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔗 Enlace de Pago (Compartir con el cliente)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={paymentData.url}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => handleCopyToClipboard(paymentData.url)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      copySuccess 
                        ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {copySuccess ? '✅ Copiado' : '📋 Copiar'}
                  </button>
                </div>
                {copySuccess && (
                  <p className="text-green-600 text-sm mt-1">✅ Enlace copiado al portapapeles</p>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-blue-900 mb-2">📱 ¿Cómo enviar al cliente?</h3>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• <strong>WhatsApp:</strong> Copia y pega el enlace en el chat</li>
                  <li>• <strong>Email:</strong> Incluye el enlace en tu email</li>
                  <li>• <strong>SMS:</strong> Envía el enlace por mensaje</li>
                  <li>• <strong>Presencial:</strong> Muestra el código QR (próximamente)</li>
                </ul>
              </div>

              {/* Bank Info Preview */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900 mb-2">🏦 El cliente verá:</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Beneficiario:</strong> Investments Made Easy, S.L.</div>
                  <div><strong>IBAN:</strong> ES11 0182 1921 2102 0154 2238</div>
                  <div><strong>Banco:</strong> BBVA</div>
                  <div><strong>Importe:</strong> {paymentData.amount.toLocaleString('es-ES')} EUR</div>
                  <div><strong>Transferencia:</strong> SEPA Inmediata (llega en segundos)</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => window.open(paymentData.url, '_blank')}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Vista Previa del Enlace
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsManager;